import { useState } from "react"
import { createPortal } from "react-dom"
import { useFirestore, useFirestoreCollectionData } from "reactfire"
import { collection, doc, deleteDoc, addDoc, writeBatch } from "firebase/firestore"
import { LISTA_UNIDADES, UNIDADES } from "../config/unidades"
import CalendarioModal from "./CalendarioModal"
import { CATEGORIAS_EVENTO } from "../config/categoriasEvento"

const DIAS_SEMANA   = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"]
const MESES         = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"]

function getCatConfig(value) {
  return CATEGORIAS_EVENTO.find(c => c.value === value) || CATEGORIAS_EVENTO[1]
}

// Devuelve todos los días que cubre un evento (array de strings "YYYY-MM-DD")
function diasDelEvento(evento) {
  const dias = []
  const inicio = new Date(evento.fechaInicio + "T12:00:00")
  const fin    = new Date((evento.fechaFin || evento.fechaInicio) + "T12:00:00")
  const cur    = new Date(inicio)
  while (cur <= fin) {
    dias.push(cur.toISOString().slice(0, 10))
    cur.setDate(cur.getDate() + 1)
  }
  return dias
}

// Hook para cargar eventos de una unidad
function useEventos(firestore, unidadId) {
  const { data, status } = useFirestoreCollectionData(
    collection(firestore, "unidades", unidadId, "eventos"),
    { idField: "id" }
  )
  return { eventos: data || [], status }
}

// Calendario de UNA unidad
function CalendarioUnidad({ unidadId, puedeEditar, esGlobal = false }) {
  const firestore = useFirestore()
  const { eventos } = useEventos(firestore, unidadId)

  const hoy     = new Date()
  const [anio,  setAnio]  = useState(hoy.getFullYear())
  const [mes,   setMes]   = useState(hoy.getMonth())
  const [modal, setModal] = useState(null) // { fecha, evento }
  const [detalle, setDetalle] = useState(null) // evento seleccionado

  const cfgUnidad = UNIDADES[unidadId]

  const irMes = (delta) => {
    let m = mes + delta
    let a = anio
    if (m < 0)  { m = 11; a-- }
    if (m > 11) { m = 0;  a++ }
    setMes(m); setAnio(a)
  }

  // Construir grilla del mes
  const primerDia  = new Date(anio, mes, 1)
  const diasMes    = new Date(anio, mes + 1, 0).getDate()
  // lunes=0 ... domingo=6
  let offsetInicio = primerDia.getDay() - 1
  if (offsetInicio < 0) offsetInicio = 6

  // Mapear eventos del mes actual a sus días
  const eventosPorDia = {}
  eventos.forEach(ev => {
    diasDelEvento(ev).forEach(dStr => {
      if (!eventosPorDia[dStr]) eventosPorDia[dStr] = []
      eventosPorDia[dStr].push(ev)
    })
  })

  const hoyStr = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,"0")}-${String(hoy.getDate()).padStart(2,"0")}`

  const eliminar = async (ev) => {
    if (!window.confirm(`¿Eliminar "${ev.titulo}"?`)) return
    await deleteDoc(doc(firestore, "unidades", unidadId, "eventos", ev.id))
    setDetalle(null)
  }

  return (
    <div>
      {/* ── Header del mes ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        {esGlobal && cfgUnidad && (
          <span className="badge" style={{ background: cfgUnidad.bg, color: cfgUnidad.texto, fontSize: 14 }}>
            {cfgUnidad.icono} {cfgUnidad.nombre}
          </span>
        )}
        <button onClick={() => irMes(-1)} style={{ background: "none", border: "1px solid var(--gris-borde)", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 16 }}>‹</button>
        <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 18, color: "var(--verde)", minWidth: 180, textAlign: "center" }}>
          {MESES[mes]} {anio}
        </span>
        <button onClick={() => irMes(1)}  style={{ background: "none", border: "1px solid var(--gris-borde)", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 16 }}>›</button>
        <button onClick={() => { setAnio(hoy.getFullYear()); setMes(hoy.getMonth()) }}
          style={{ marginLeft: 8, padding: "4px 12px", borderRadius: 8, border: "1px solid var(--gris-borde)", background: "none", cursor: "pointer", fontSize: 12, color: "var(--gris)" }}>
          Hoy
        </button>
        {puedeEditar && (
          <button
            onClick={() => setModal({ fecha: new Date() })}
            className="btn-primary"
            style={{ marginLeft: "auto", fontSize: 13, padding: "7px 16px" }}
          >
            + Nuevo evento para todas
          </button>
        )}
      </div>

      {/* ── Grilla ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 20 }}>
        {/* Cabecera días semana */}
        {DIAS_SEMANA.map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: "var(--gris)", padding: "6px 0", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {d}
          </div>
        ))}

        {/* Espacios vacíos antes del día 1 */}
        {Array.from({ length: offsetInicio }).map((_, i) => (
          <div key={`empty-${i}`} style={{ minHeight: 80, background: "#fafafa", borderRadius: 8 }} />
        ))}

        {/* Días del mes */}
        {Array.from({ length: diasMes }).map((_, i) => {
          const dia     = i + 1
          const dStr    = `${anio}-${String(mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`
          const esHoy   = dStr === hoyStr
          const evsDia  = eventosPorDia[dStr] || []

          return (
            <div
              key={dStr}
              onClick={() => puedeEditar && setModal({ fecha: new Date(anio, mes, dia), evento: null })}
              style={{
                minHeight: 80, borderRadius: 8, padding: "6px 8px",
                background: esHoy ? "var(--verde-suave)" : "#fff",
                border: esHoy ? "2px solid var(--verde)" : "1px solid var(--gris-borde)",
                cursor: puedeEditar ? "pointer" : "default",
                transition: "box-shadow 0.15s",
                position: "relative",
              }}
              onMouseEnter={e => { if (puedeEditar) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)" }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "none" }}
            >
              {/* Número del día */}
              <div style={{
                fontSize: 13, fontWeight: esHoy ? 800 : 500,
                color: esHoy ? "var(--verde)" : "var(--texto)",
                marginBottom: 4,
              }}>
                {dia}
              </div>

              {/* Eventos del día */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {evsDia.slice(0, 3).map(ev => {
                  const cat = getCatConfig(ev.categoria)
                  return (
                    <div
                      key={ev.id}
                      onClick={e => { e.stopPropagation(); setDetalle(ev) }}
                      style={{
                        fontSize: 11, fontWeight: 600, borderRadius: 4,
                        padding: "2px 5px", background: cat.bg, color: cat.color,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        cursor: "pointer",
                      }}
                      title={ev.titulo}
                    >
                      {cat.icono} {ev.titulo}
                    </div>
                  )
                })}
                {evsDia.length > 3 && (
                  <div style={{ fontSize: 10, color: "var(--gris)", paddingLeft: 4 }}>
                    +{evsDia.length - 3} más
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Lista de eventos del mes ── */}
      {(() => {
        const evsMes = eventos.filter(ev =>
          ev.fechaInicio?.startsWith(`${anio}-${String(mes+1).padStart(2,"0")}`) ||
          ev.fechaFin?.startsWith(`${anio}-${String(mes+1).padStart(2,"0")}`)
        ).sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio))

        if (evsMes.length === 0) return null

        return (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--gris)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Eventos de {MESES[mes]}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {evsMes.map(ev => {
                const cat = getCatConfig(ev.categoria)
                return (
                  <div
                    key={ev.id}
                    onClick={() => setDetalle(ev)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      background: "#fff", border: "1px solid var(--gris-borde)",
                      borderLeft: `4px solid ${cat.color}`,
                      borderRadius: 10, padding: "12px 16px", cursor: "pointer",
                      transition: "box-shadow 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: cat.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                      {cat.icono}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{ev.titulo}</div>
                      <div style={{ fontSize: 12, color: "var(--gris)", marginTop: 2 }}>
                        {ev.fechaInicio === ev.fechaFin || !ev.fechaFin
                          ? formatFecha(ev.fechaInicio)
                          : `${formatFecha(ev.fechaInicio)} → ${formatFecha(ev.fechaFin)}`
                        }
                        {!ev.todoElDia && ev.horaInicio && ` · ${ev.horaInicio}${ev.horaFin ? ` - ${ev.horaFin}` : ""}`}
                      </div>
                    </div>
                    <span className="badge" style={{ background: cat.bg, color: cat.color, fontSize: 11 }}>
                      {cat.label}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* ── Modal crear/editar ── */}
      {modal && createPortal(
        <CalendarioModal
          unidadId={unidadId}
          fecha={modal.fecha}
          evento={modal.evento}
          onCerrar={() => setModal(null)}
        />,
        document.body
      )}

      {/* ── Detalle de evento ── */}
      {detalle && createPortal(
        <DetalleEvento
          evento={detalle}
          unidadId={unidadId}
          puedeEditar={puedeEditar}
          onEditar={() => { setModal({ fecha: null, evento: detalle }); setDetalle(null) }}
          onEliminar={() => eliminar(detalle)}
          onCerrar={() => setDetalle(null)}
        />,
        document.body
      )}
    </div>
  )
}

// Modal de detalle de un evento
function DetalleEvento({ evento, unidadId, puedeEditar, onEditar, onEliminar, onCerrar }) {
  const cat = getCatConfig(evento.categoria)
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-box" style={{ maxWidth: 460 }}>
        <button className="modal-cerrar" onClick={onCerrar}>×</button>

        {/* Cabecera coloreada */}
        <div style={{ background: cat.bg, borderRadius: 12, padding: "16px 20px", marginBottom: 20, borderLeft: `4px solid ${cat.color}` }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>{cat.icono}</div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: cat.color }}>{evento.titulo}</div>
          <div style={{ fontSize: 12, color: cat.color, opacity: 0.8, marginTop: 4 }}>{cat.label}</div>
        </div>

        {/* Fechas */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--texto-suave)" }}>
            📅 {evento.fechaInicio === evento.fechaFin || !evento.fechaFin
              ? formatFecha(evento.fechaInicio)
              : `${formatFecha(evento.fechaInicio)} → ${formatFecha(evento.fechaFin)}`
            }
          </span>
          {!evento.todoElDia && evento.horaInicio && (
            <span style={{ fontSize: 13, color: "var(--texto-suave)" }}>
              🕐 {evento.horaInicio}{evento.horaFin ? ` - ${evento.horaFin}` : ""}
            </span>
          )}
          {evento.todoElDia && (
            <span style={{ fontSize: 12, color: "var(--gris)", fontStyle: "italic" }}>Todo el día</span>
          )}
        </div>

        {/* Descripción */}
        {evento.descripcion && (
          <div style={{ fontSize: 14, color: "var(--texto-suave)", lineHeight: 1.6, marginBottom: 16, fontStyle: "italic" }}>
            💬 {evento.descripcion}
          </div>
        )}

        {puedeEditar && (
          <div className="form-actions">
            <button className="btn-icon" style={{ fontSize: 20 }} onClick={onEliminar}>🗑️</button>
            <button className="btn-secondary" onClick={onCerrar}>Cerrar</button>
            <button className="btn-primary" style={{ background: cat.color }} onClick={onEditar}>✏️ Editar</button>
          </div>
        )}
        {!puedeEditar && (
          <div style={{ textAlign: "right" }}>
            <button className="btn-secondary" onClick={onCerrar}>Cerrar</button>
          </div>
        )}
      </div>
    </div>
  )
}

function formatFecha(str) {
  if (!str) return ""
  const [a, m, d] = str.split("-")
  return `${d}/${m}/${a}`
}

// ══════════════════════════════════════════════════════
// Hook que carga eventos de TODAS las unidades a la vez
// ══════════════════════════════════════════════════════
function useEventosTodas(firestore) {
  const todasLasUnidades = LISTA_UNIDADES // bandada, manada, ..., consejo

  // Un hook por unidad — React requiere cantidad fija de hooks
  const bandada   = useFirestoreCollectionData(collection(firestore, "unidades", "bandada",   "eventos"), { idField: "id" })
  const manada    = useFirestoreCollectionData(collection(firestore, "unidades", "manada",    "eventos"), { idField: "id" })
  const compania  = useFirestoreCollectionData(collection(firestore, "unidades", "compania",  "eventos"), { idField: "id" })
  const tropa     = useFirestoreCollectionData(collection(firestore, "unidades", "tropa",     "eventos"), { idField: "id" })
  const avanzada  = useFirestoreCollectionData(collection(firestore, "unidades", "avanzada",  "eventos"), { idField: "id" })
  const clan      = useFirestoreCollectionData(collection(firestore, "unidades", "clan",      "eventos"), { idField: "id" })
  const consejo   = useFirestoreCollectionData(collection(firestore, "unidades", "consejo",   "eventos"), { idField: "id" })

  const porUnidad = { bandada, manada, compania, tropa, avanzada, clan, consejo }
  const loading   = Object.values(porUnidad).some(r => r.status === "loading")

  // Combinar todos los eventos etiquetando con unidadId
  const todos = Object.entries(porUnidad).flatMap(([uid, result]) =>
    (result.data || []).map(ev => ({ ...ev, unidadId: uid }))
  )

  return { todos, loading }
}

// ══════════════════════════════════════════════════════
// Calendario Global del Consejo — un solo calendario
// que muestra eventos de todas las unidades
// ══════════════════════════════════════════════════════
function CalendarioGlobal({ puedeEditar }) {
  const firestore = useFirestore()
  const { todos: eventos, loading } = useEventosTodas(firestore)

  const hoy = new Date()
  const [anio,    setAnio]    = useState(hoy.getFullYear())
  const [mes,     setMes]     = useState(hoy.getMonth())
  const [modal,   setModal]   = useState(null)   // { fecha }
  const [detalle, setDetalle] = useState(null)   // evento

  const irMes = (delta) => {
    let m = mes + delta, a = anio
    if (m < 0)  { m = 11; a-- }
    if (m > 11) { m = 0;  a++ }
    setMes(m); setAnio(a)
  }

  const primerDia  = new Date(anio, mes, 1)
  const diasMes    = new Date(anio, mes + 1, 0).getDate()
  let offsetInicio = primerDia.getDay() - 1
  if (offsetInicio < 0) offsetInicio = 6

  const hoyStr = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,"0")}-${String(hoy.getDate()).padStart(2,"0")}`

  // Mapear todos los eventos a sus días
  const eventosPorDia = {}
  eventos.forEach(ev => {
    diasDelEvento(ev).forEach(dStr => {
      if (!eventosPorDia[dStr]) eventosPorDia[dStr] = []
      eventosPorDia[dStr].push(ev)
    })
  })

  // Guardar en TODAS las unidades simultáneamente
  const guardarEnTodas = async (datos) => {
    const todasIds = ["bandada", "manada", "compania", "tropa", "avanzada", "clan", "consejo"]
    await Promise.all(todasIds.map(uid =>
      addDoc(collection(firestore, "unidades", uid, "eventos"), {
        ...datos,
        unidadId: uid,
        creadoEn: { seconds: Math.floor(Date.now() / 1000) },
      })
    ))
  }

  const eliminar = async (ev) => {
    if (!window.confirm(`¿Eliminar "${ev.titulo}" solo de ${UNIDADES[ev.unidadId]?.nombre || ev.unidadId}?

Si fue creado desde el Consejo y quieres eliminarlo de todas las unidades, hazlo desde cada una.`)) return
    await deleteDoc(doc(firestore, "unidades", ev.unidadId, "eventos", ev.id))
    setDetalle(null)
  }

  if (loading) return <div className="loading">Cargando calendario...</div>

  return (
    <div>
      {/* Banner */}
      <div style={{
        background: "#ede9fe", border: "1px solid #c4b5fd",
        borderRadius: 12, padding: "14px 18px", marginBottom: 20,
        fontSize: 13, color: "#5b21b6", display: "flex", gap: 12, alignItems: "flex-start",
      }}>
        <span style={{ fontSize: 20 }}>🏛️</span>
        <div>
          Vista unificada de <strong>todas las unidades</strong>. Los eventos creados aquí se agregan automáticamente a los calendarios de todas las unidades.
          Cada evento muestra el badge de color de su unidad.
        </div>
      </div>

      {/* Header del mes */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={() => irMes(-1)} style={{ background: "none", border: "1px solid var(--gris-borde)", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 16 }}>‹</button>
        <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 18, color: "var(--verde)", minWidth: 180, textAlign: "center" }}>
          {MESES[mes]} {anio}
        </span>
        <button onClick={() => irMes(1)}  style={{ background: "none", border: "1px solid var(--gris-borde)", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 16 }}>›</button>
        <button onClick={() => { setAnio(hoy.getFullYear()); setMes(hoy.getMonth()) }}
          style={{ marginLeft: 8, padding: "4px 12px", borderRadius: 8, border: "1px solid var(--gris-borde)", background: "none", cursor: "pointer", fontSize: 12, color: "var(--gris)" }}>
          Hoy
        </button>
        {puedeEditar && (
          <button
            onClick={() => setModal({ fecha: new Date() })}
            className="btn-primary"
            style={{ marginLeft: "auto", fontSize: 13, padding: "7px 16px" }}
          >
            + Nuevo evento para todas
          </button>
        )}
      </div>

      {/* Leyenda unidades */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {LISTA_UNIDADES.map(u => (
          <span key={u.id} className="badge" style={{ background: u.bg, color: u.texto, fontSize: 11 }}>
            {u.icono} {u.nombre}
          </span>
        ))}
      </div>

      {/* Grilla */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 24 }}>
        {DIAS_SEMANA.map(d => (
          <div key={d} style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: "var(--gris)", padding: "6px 0", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {d}
          </div>
        ))}
        {Array.from({ length: offsetInicio }).map((_, i) => (
          <div key={`e-${i}`} style={{ minHeight: 88, background: "#fafafa", borderRadius: 8 }} />
        ))}
        {Array.from({ length: diasMes }).map((_, i) => {
          const dia    = i + 1
          const dStr   = `${anio}-${String(mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`
          const esHoy  = dStr === hoyStr
          const evsDia = eventosPorDia[dStr] || []

          return (
            <div
              key={dStr}
              onClick={() => puedeEditar && setModal({ fecha: new Date(anio, mes, dia) })}
              style={{
                minHeight: 88, borderRadius: 8, padding: "6px 8px",
                background: esHoy ? "var(--verde-suave)" : "#fff",
                border: esHoy ? "2px solid var(--verde)" : "1px solid var(--gris-borde)",
                cursor: puedeEditar ? "pointer" : "default",
              }}
              onMouseEnter={e => { if (puedeEditar) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)" }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "none" }}
            >
              <div style={{ fontSize: 13, fontWeight: esHoy ? 800 : 500, color: esHoy ? "var(--verde)" : "var(--texto)", marginBottom: 4 }}>
                {dia}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {evsDia.slice(0, 3).map(ev => {
                  const cat = getCatConfig(ev.categoria)
                  const cfg = UNIDADES[ev.unidadId]
                  return (
                    <div
                      key={ev.id}
                      onClick={e => { e.stopPropagation(); setDetalle(ev) }}
                      style={{
                        fontSize: 11, fontWeight: 600, borderRadius: 4,
                        padding: "2px 5px", cursor: "pointer",
                        background: cfg ? cfg.bg : cat.bg,
                        color:      cfg ? cfg.texto : cat.color,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}
                      title={`${cfg?.nombre || ""}: ${ev.titulo}`}
                    >
                      {cfg?.icono} {ev.titulo}
                    </div>
                  )
                })}
                {evsDia.length > 3 && (
                  <div style={{ fontSize: 10, color: "var(--gris)", paddingLeft: 4 }}>+{evsDia.length - 3} más</div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Lista de eventos del mes */}
      {(() => {
        const evsMes = eventos
          .filter(ev =>
            ev.fechaInicio?.startsWith(`${anio}-${String(mes+1).padStart(2,"0")}`) ||
            ev.fechaFin?.startsWith(`${anio}-${String(mes+1).padStart(2,"0")}`)
          )
          .sort((a, b) => a.fechaInicio.localeCompare(b.fechaInicio))

        if (evsMes.length === 0) return (
          <div className="empty-state" style={{ padding: "32px 20px" }}>
            <div className="empty-icon">📅</div>
            <div className="empty-text">No hay eventos en {MESES[mes]}.</div>
          </div>
        )

        return (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--gris)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Eventos de {MESES[mes]} — todas las unidades
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {evsMes.map(ev => {
                const cat = getCatConfig(ev.categoria)
                const cfg = UNIDADES[ev.unidadId]
                return (
                  <div
                    key={ev.id + ev.unidadId}
                    onClick={() => setDetalle(ev)}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      background: "#fff", border: "1px solid var(--gris-borde)",
                      borderLeft: `4px solid ${cfg ? cfg.color : cat.color}`,
                      borderRadius: 10, padding: "12px 16px", cursor: "pointer",
                    }}
                    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 8, background: cfg ? cfg.bg : cat.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                      {cfg?.icono || cat.icono}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{ev.titulo}</div>
                      <div style={{ fontSize: 12, color: "var(--gris)", marginTop: 2 }}>
                        {ev.fechaInicio === ev.fechaFin || !ev.fechaFin
                          ? formatFecha(ev.fechaInicio)
                          : `${formatFecha(ev.fechaInicio)} → ${formatFecha(ev.fechaFin)}`
                        }
                        {!ev.todoElDia && ev.horaInicio && ` · ${ev.horaInicio}${ev.horaFin ? ` - ${ev.horaFin}` : ""}`}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      {cfg && <span className="badge" style={{ background: cfg.bg, color: cfg.texto, fontSize: 11 }}>{cfg.icono} {cfg.nombre}</span>}
                      <span className="badge" style={{ background: cat.bg, color: cat.color, fontSize: 11 }}>{cat.icono} {cat.label}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {/* Modal crear — guarda en TODAS las unidades */}
      {modal && createPortal(
        <CalendarioModal
          unidadId="consejo"
          fecha={modal.fecha}
          evento={null}
          onGuardarOverride={guardarEnTodas}
          onCerrar={() => setModal(null)}
        />,
        document.body
      )}

      {/* Detalle */}
      {detalle && createPortal(
        <DetalleEvento
          evento={detalle}
          unidadId={detalle.unidadId}
          puedeEditar={puedeEditar}
          onEditar={() => {}}
          onEliminar={() => eliminar(detalle)}
          onCerrar={() => setDetalle(null)}
        />,
        document.body
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════
// Componente exportado principal
// ══════════════════════════════════════════════════════
export default function Calendario({ unidadId, puedeEditar }) {
  const esGlobal = unidadId === "consejo"

  const leyenda = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
      {CATEGORIAS_EVENTO.map(cat => (
        <span key={cat.value} style={{
          fontSize: 12, fontWeight: 600, padding: "4px 10px",
          borderRadius: 8, background: cat.bg, color: cat.color,
        }}>
          {cat.icono} {cat.label}
        </span>
      ))}
    </div>
  )

  if (!esGlobal) {
    return (
      <div>
        {leyenda}
        <CalendarioUnidad unidadId={unidadId} puedeEditar={puedeEditar} />
      </div>
    )
  }

  return (
    <div>
      {leyenda}
      <CalendarioGlobal puedeEditar={puedeEditar} />
    </div>
  )
}
